import { Close as CloseIcon, ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import FieldMappingSelector from "../../../components/FieldMappingSelector";
import { hostname, parseError } from "../../../utils";

interface FieldMapping {
  email?: string[];
  phone?: string[];
  linkedinUrl?: string[];
  address?: string[];
  city?: string[];
  state?: string[];
  zip?: string[];
}

interface ListHeader {
  id: string;
  name: string;
  type?: string;
}

interface ImportFromSmartListDialogProps {
  open: boolean;
  onClose: () => void;
  sequence: any;
  companyId: string;
  getToken: () => Promise<string>;
  eventListener: Function;
  onViewMembers?: () => void;
}

const SKIP_REASON_LABELS: Record<string, string> = {
  missing_email: "Missing email address",
  missing_first_name: "Missing first name",
  duplicate_email: "Already in sequence (matched by email)",
  duplicate_linkedin: "Already in sequence (matched by LinkedIn URL)",
  duplicate_phone: "Already in sequence (matched by phone)",
  integrity_error: "Already in sequence (duplicate key)",
  other: "Other",
};

const SKIP_REASON_VALUE_NOUNS: Record<string, string> = {
  duplicate_email: "email address",
  duplicate_linkedin: "LinkedIn URL",
  duplicate_phone: "phone number",
};

interface SkipExample {
  rowId: string;
  matchedMemberId?: string | null;
  matchedMemberName?: string | null;
  message?: string | null;
}

const ImportFromSmartListDialog: React.FC<ImportFromSmartListDialogProps> = ({
  open,
  onClose,
  sequence,
  companyId,
  getToken,
  eventListener,
  onViewMembers,
}) => {
  const [lists, setLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState("");
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [progress, setProgress] = useState<any>(null);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping>({});
  const [listHeaders, setListHeaders] = useState<ListHeader[]>([]);
  const [showFieldMappings, setShowFieldMappings] = useState(false);

  useEffect(() => {
    if (open) {
      loadLists();
      setSelectedListId("");
      setImportResult(null);
      setProgress(null);
      setFieldMappings({});
      setListHeaders([]);
      setShowFieldMappings(false);
    }
  }, [open]);

  // Load headers when list is selected
  useEffect(() => {
    if (selectedListId) {
      loadListHeaders(selectedListId);
    } else {
      setListHeaders([]);
    }
  }, [selectedListId]);

  const loadListHeaders = async (listId: string) => {
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/lists/${listId}`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });
      const listData = response.data?.list || response.data;
      if (listData && listData.headers) {
        // Filter out action type headers
        const headers = listData.headers.filter(
          (h: any) => h.type !== "action"
        );
        setListHeaders(headers);
      }
    } catch (err) {
      console.error("Failed to load list headers:", err);
    }
  };

  const loadLists = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/lists`, {
        params: { companyId, page: 1, pageSize: 100 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setLists(response.data.items);
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    }
  };

  const handleImport = async () => {
    if (!selectedListId || !sequence) {
      return;
    }

    setLoading(true);
    setImportResult(null);
    setProgress(null);

    try {
      const token = await getToken();

      // Build request body with optional field mappings
      const requestBody: any = {
        companyId,
        listId: selectedListId,
      };

      // Only include fieldMappings if any are configured
      if (
        fieldMappings.email?.length ||
        fieldMappings.phone?.length ||
        fieldMappings.linkedinUrl?.length ||
        fieldMappings.address?.length ||
        fieldMappings.city?.length ||
        fieldMappings.state?.length ||
        fieldMappings.zip?.length
      ) {
        requestBody.fieldMappings = fieldMappings;
      }

      // Use fetch with ReadableStream for SSE with POST
      const response = await fetch(
        `${hostname}/sequences/${sequence.id}/import-from-list`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to start import");
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error("No response body");
      }

      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        // Decode the chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages in the buffer
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.substring(6));

              if (data.type === "start") {
                setProgress({
                  total: data.total,
                  processed: 0,
                  imported: 0,
                  skipped: 0,
                  errors: 0,
                });
              } else if (data.type === "progress") {
                setProgress({
                  total: data.total,
                  processed: data.processed,
                  imported: data.imported,
                  updated: data.updated,
                  skipped: data.skipped,
                  errors: data.errors,
                  skipReasons: data.skipReasons,
                  uniqueMatchCounts: data.uniqueMatchCounts,
                });
              } else if (data.type === "complete") {
                setImportResult(data);
                setLoading(false);
              } else if (data.type === "error") {
                throw new Error(data.message);
              }
            } catch (parseError) {
              console.error("Error parsing SSE data:", parseError);
            }
          }
        }
      }
    } catch (err) {
      setLoading(false);
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    }
  };

  const handleClose = () => {
    setSelectedListId("");
    setImportResult(null);
    setProgress(null);
    setLoading(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Import from Smart List
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select a Smart List to import members into{" "}
            <strong>{sequence?.name}</strong>. Members with matching email
            addresses will be updated, and new members will be added.
          </Typography>

          <FormControl fullWidth>
            <InputLabel>Smart List</InputLabel>
            <Select
              label="Smart List"
              value={selectedListId}
              onChange={(e) => setSelectedListId(e.target.value)}
              disabled={loading}
            >
              {lists.map((list: any) => (
                <MenuItem key={list.id} value={list.id}>
                  {list.name} ({list.count || 0} rows)
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Advanced Section */}
          {selectedListId && listHeaders.length > 0 && !loading && !importResult && (
            <Accordion
              expanded={showFieldMappings}
              onChange={() => setShowFieldMappings(!showFieldMappings)}
              sx={{ mt: 2, boxShadow: "none", "&:before": { display: "none" } }}
              disableGutters
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ px: 0, minHeight: "auto", "& .MuiAccordionSummary-content": { my: 1 } }}
              >
                <Typography variant="body2" color="text.secondary">
                  Advanced
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Field Mapping
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Customize which fields are used for contact info and address.
                  Fields are tried in the order you select them.
                </Typography>
                <FieldMappingSelector
                  fieldMappings={fieldMappings}
                  onChange={setFieldMappings}
                  availableHeaders={listHeaders}
                  showHelp={false}
                />
              </AccordionDetails>
            </Accordion>
          )}

          {progress && !importResult && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Importing members... {progress.processed} of {progress.total}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(progress.processed / progress.total) * 100}
                sx={{ mb: 2 }}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="caption" color="primary.main">
                  ✓ New: {progress.imported}
                </Typography>
                <Typography variant="caption" color="info.main">
                  ↻ Updated existing: {progress.updated || 0}
                </Typography>
                <Typography variant="caption" color="warning.main">
                  ⊘ Skipped: {progress.skipped}
                </Typography>
                <Typography variant="caption" color="error.main">
                  ✗ Errors: {progress.errors}
                </Typography>
              </Box>
              {progress.skipReasons &&
                Object.keys(progress.skipReasons).length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {Object.entries(
                      progress.skipReasons as Record<string, number>
                    )
                      .sort((a, b) => b[1] - a[1])
                      .map(([reason, count]) => {
                        const distinct = (progress.uniqueMatchCounts || {})[
                          reason
                        ];
                        const valueNoun = SKIP_REASON_VALUE_NOUNS[reason];
                        return (
                          <Typography
                            key={reason}
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            – {count} {SKIP_REASON_LABELS[reason] || reason}
                            {distinct !== undefined && valueNoun && (
                              <>
                                {" "}
                                — only <strong>{distinct}</strong> distinct{" "}
                                {distinct === 1 ? valueNoun : `${valueNoun}s`}
                              </>
                            )}
                          </Typography>
                        );
                      })}
                  </Box>
                )}
            </Box>
          )}

          {importResult && (() => {
            const updatedCount = importResult.updated || 0;
            const totalProcessed =
              (importResult.imported || 0) +
              updatedCount +
              (importResult.skipped || 0) +
              (importResult.errors || 0);
            const uniqueMatchCounts: Record<string, number> =
              importResult.uniqueMatchCounts || {};
            const reasonEntries: [string, number][] = importResult.skipReasons
              ? Object.entries(importResult.skipReasons as Record<string, number>).sort(
                  (a, b) => b[1] - a[1]
                )
              : [];
            return (
              <Alert
                severity={
                  importResult.errors > 0
                    ? "warning"
                    : updatedCount > 0 || importResult.skipped > 0
                    ? "info"
                    : "success"
                }
                sx={{ mt: 2 }}
              >
                <Typography variant="body2">
                  <strong>Import Results</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • Rows processed: {totalProcessed}
                </Typography>
                <Typography variant="body2">
                  • New members added: {importResult.imported}
                </Typography>
                {updatedCount > 0 && (
                  <Typography variant="body2">
                    • Rows that updated an existing member: {updatedCount}
                  </Typography>
                )}
                <Typography variant="body2">
                  • Skipped: {importResult.skipped}
                </Typography>
                {reasonEntries.length > 0 && (
                  <Box sx={{ mt: 1, ml: 2 }}>
                    {reasonEntries.map(([reason, count]) => {
                      const examples: SkipExample[] =
                        (importResult.skipExamples &&
                          importResult.skipExamples[reason]) ||
                        [];
                      const distinct = uniqueMatchCounts[reason];
                      const valueNoun = SKIP_REASON_VALUE_NOUNS[reason];
                      return (
                        <Box key={reason} sx={{ mb: 0.5 }}>
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            – {count} {SKIP_REASON_LABELS[reason] || reason}
                            {distinct !== undefined && valueNoun && (
                              <>
                                {" "}
                                — only <strong>{distinct}</strong> distinct{" "}
                                {distinct === 1 ? valueNoun : `${valueNoun}s`}
                              </>
                            )}
                          </Typography>
                          {examples.length > 0 && (
                            <Box sx={{ ml: 2 }}>
                              {examples.map((ex, idx) => (
                                <Typography
                                  key={`${reason}-${idx}`}
                                  variant="caption"
                                  display="block"
                                  color="text.secondary"
                                >
                                  •{" "}
                                  {ex.matchedMemberName
                                    ? `Matched: ${ex.matchedMemberName}`
                                    : ex.message || `Row ${ex.rowId}`}
                                </Typography>
                              ))}
                              {count > examples.length && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color="text.secondary"
                                  sx={{ fontStyle: "italic" }}
                                >
                                  + {count - examples.length} more
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
                {importResult.errors > 0 && (
                  <>
                    <Typography variant="body2" color="error">
                      • Errors: {importResult.errors}
                    </Typography>
                    {importResult.errorDetails &&
                      importResult.errorDetails.length > 0 && (
                        <Box sx={{ mt: 1, ml: 2 }}>
                          {importResult.errorDetails.map(
                            (error: string, index: number) => (
                              <Typography
                                key={index}
                                variant="caption"
                                display="block"
                                color="error"
                              >
                                {error}
                              </Typography>
                            )
                          )}
                        </Box>
                      )}
                  </>
                )}
              </Alert>
            );
          })()}
        </Box>
      </DialogContent>
      <DialogActions>
        <LoadingButton onClick={handleClose} disabled={loading}>
          {importResult ? "Close" : "Cancel"}
        </LoadingButton>
        {importResult && onViewMembers && (
          <LoadingButton
            onClick={() => {
              setSelectedListId("");
              setImportResult(null);
              setProgress(null);
              setLoading(false);
              onViewMembers();
            }}
          >
            View Members
          </LoadingButton>
        )}
        {!importResult && (
          <LoadingButton
            variant="contained"
            onClick={handleImport}
            loading={loading}
            disabled={!selectedListId}
          >
            Import
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportFromSmartListDialog;
